import { KYC_STATUSES } from '@shinetools/kyc-validation';

import { KycReviewDocumentType } from '__generated__/GQL';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import {
  getKycDocStatusByCategory,
  isKycDocumentReviewable,
} from 'features/Bento/libs/kycDocuments';

import addressSchema from '../../../components/AddressForm/addressSchema';
import { AccommodationStatus } from '../libs/constants';
import { getAccommodationStatus } from '../libs/hosted';
import { Model } from './model';

export enum Guard {
  HasNoKycAddressDocument = 'hasNoKycAddressDocument',
  IsAddressComplete = 'isAddressComplete',
  IsHostedFlowComplete = 'isHostedFlowComplete',
  IsHostedPoaStepDone = 'isHostedPoaStepDone',
  IsProofOfAddressReviewable = 'isProofOfAddressReviewable',
  IsUserSelfHosted = 'isUserSelfHosted',
}

const guards: MachineGuards<Model, Guard> = {
  hasNoKycAddressDocument: ({ getModuleData }) => {
    const { kycDocuments } = getModuleData().viewer.companyUser;

    const accommodationStatus = getAccommodationStatus({ kycDocuments });

    return accommodationStatus === null;
  },

  isAddressComplete: (context) =>
    addressSchema.safeParse(context.address).success,

  isHostedFlowComplete: ({ getModuleData }) => {
    const { kycDocuments } = getModuleData().viewer.companyUser;

    const steps = [
      'ADDRESS_HOST',
      'PROOF_OF_ACCOMMODATION',
      'ID_HOST',
    ] as const;

    return steps.every((step) =>
      [KYC_STATUSES.PENDING, KYC_STATUSES.VALIDATED].includes(
        getKycDocStatusByCategory({ kycDocuments }, step),
      ),
    );
  },

  isHostedPoaStepDone: ({ getModuleData }) => {
    const { kycDocuments } = getModuleData().viewer.companyUser;

    const status = getKycDocStatusByCategory({ kycDocuments }, 'ADDRESS_HOST');

    return [KYC_STATUSES.PENDING, KYC_STATUSES.VALIDATED].includes(status);
  },

  isProofOfAddressReviewable: ({ getModuleData }) => {
    // Can't use getKycDocStatusByCategory here as the ADDRESS kyc category argument
    // will make the function loop through hosted documents
    const { kycDocuments } = getModuleData().viewer.companyUser;
    const proofOfAddressDocument = kycDocuments.find(
      (doc) => doc?.type === KycReviewDocumentType.ProofOfAddress,
    );

    return Boolean(
      proofOfAddressDocument && isKycDocumentReviewable(proofOfAddressDocument),
    );
  },

  isUserSelfHosted: ({ getModuleData }) => {
    const { kycDocuments } = getModuleData().viewer.companyUser;

    const accommodationStatus = getAccommodationStatus({ kycDocuments });

    return accommodationStatus === AccommodationStatus.SELF_HOSTED;
  },
};

export default guards;
